import { useMutation, useQueryClient } from "@tanstack/react-query";
import { cloneDeep } from "lodash";
import { Crop, DropSize, Task, TillingDepth } from "./constants";
import { karlFarmPost, karlFarmQueryClient } from "./queryClient";
import { AllTasksResponse, getUseAllTasksQueryKey } from "./useAllTasks";

// Route: POST /data/tasks/{id}
// API Docs: https://documenter.getpostman.com/view/16381164/UVksNF2z#81d807c8-ae25-49b9-ba6d-b6672a0235a1

const getEditTaskPath = (id: string) => `/data/tasks/${id}`;

export type EditTaskRequestBody = {
  attributes: {
    title: string;
    task: Task;
    crop?: Crop;
    tilling_depth?: TillingDepth;
    drop_size?: DropSize;
    notes?: string;
  };
  time: {
    /** UNIX epoch in seconds */
    start: number;
    /** In minutes */
    duration: number;
    /** UNIX epoch in seconds */
    earliest?: number;
    /** UNIX epoch in seconds */
    deadline?: number;
  };
  fields: Array<{ id: string }>;
  users: Array<{ id: string }>;
  machines: Array<{ id: string }>;
};

export const useEditTaskMutation = () => {
  const queryClient = useQueryClient();
  const tasksQueryKey = getUseAllTasksQueryKey();

  type EditTaskMutationVariables = {
    id: string;
    body: EditTaskRequestBody;
  };
  return useMutation(
    ({ id, body }: EditTaskMutationVariables) =>
      karlFarmPost(getEditTaskPath(id), body),
    {
      onMutate: async ({ id, body }: EditTaskMutationVariables) => {
        await queryClient.cancelQueries(tasksQueryKey);

        const previousTasks = queryClient.getQueryData(tasksQueryKey);

        editTaskQueryData(id, body);

        return { previousTasks };
      },
      onError: (error, _, context) => {
        queryClient.setQueryData(tasksQueryKey, context?.previousTasks);
        console.error(error);
      },
      onSettled: () => {
        queryClient.invalidateQueries(tasksQueryKey);
      },
    }
  );
};

export const editTaskQueryData = (id: string, body: EditTaskRequestBody) => {
  karlFarmQueryClient.setQueryData<AllTasksResponse>(
    getUseAllTasksQueryKey(),
    (old: AllTasksResponse | undefined) => {
      if (old == null) return undefined;
      const newTasks = cloneDeep(old);
      if (newTasks.data == null) return undefined;
      const task = newTasks.data.tasks.find((task) => task.id === id);
      const { attributes, time, fields, users, machines } = body;
      if (task != null) {
        task.attributes = attributes;
        task.time = {
          start: time.start,
          duration: time.duration,
          earliest: time.earliest || null,
          deadline: time.deadline || null,
        };
        task.fields = fields;
        task.users = users;
        task.machines = machines;
      }
      return newTasks;
    }
  );
};
